import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  Download,
  Loader2,
  Upload,
  MessageSquare,
  Receipt,
  CheckCircle2,
} from 'lucide-react';
import {
  getOrderById,
  getOrderItems,
  getOrderEvents,
  getOrderFiles,
  getOrderFileUrl,
  uploadOrderFile,
  addOrderNote,
  updateOrderCustomerNotes,
  ORDER_STATUS_LABELS,
  ORDER_STATUS_COLORS,
  formatCashPrice,
  type CommerceOrder,
  type CommerceOrderItem,
  type CommerceOrderEvent,
  type CommerceOrderFile,
} from '@rehub/commerce';
import { supabase } from '../services/supabase';

interface CommerceOrderDetailProps {
  orderId: string;
  userId: string;
}

export default function CommerceOrderDetail({ orderId, userId }: CommerceOrderDetailProps) {
  const [order, setOrder] = useState<CommerceOrder | null>(null);
  const [items, setItems] = useState<CommerceOrderItem[]>([]);
  const [events, setEvents] = useState<CommerceOrderEvent[]>([]);
  const [files, setFiles] = useState<CommerceOrderFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [paidBanner, setPaidBanner] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const reload = async () => {
    const [o, i, e, f] = await Promise.all([
      getOrderById(supabase, orderId),
      getOrderItems(supabase, orderId),
      getOrderEvents(supabase, orderId),
      getOrderFiles(supabase, orderId),
    ]);
    setOrder(o);
    setItems(i);
    setEvents(e);
    setFiles(f);
    if (o?.customer_notes) setCustomerNotes(o.customer_notes);
  };

  useEffect(() => {
    reload().finally(() => setLoading(false));
    if (new URLSearchParams(window.location.search).get('paid') === '1') {
      setPaidBanner(true);
      window.history.replaceState({}, '', `/orders/${orderId}`);
    }
  }, [orderId]);

  const canUpload =
    order && ['awaiting_files', 'submitted', 'in_progress', 'review'].includes(order.status);

  const handleUpload = async (fileList: FileList | null) => {
    if (!fileList?.length) return;
    setUploading(true);
    for (const file of Array.from(fileList)) {
      await uploadOrderFile(supabase, { orderId, userId, file });
    }
    await reload();
    setUploading(false);
  };

  const handleAddNote = async () => {
    if (!note.trim()) return;
    await addOrderNote(supabase, orderId, note.trim());
    setNote('');
    await reload();
  };

  const handleSaveNotes = async () => {
    await updateOrderCustomerNotes(supabase, orderId, customerNotes);
    await reload();
  };

  const downloadFile = async (path: string, name: string) => {
    const url = await getOrderFileUrl(supabase, path);
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="animate-spin text-violet-400" size={32} />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-black text-slate-400 flex items-center justify-center">
        Order not found
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-slate-200">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <a href="/orders" className="text-slate-400 hover:text-white flex items-center gap-1 text-sm mb-6">
          <ArrowLeft size={16} /> All orders
        </a>

        {paidBanner && (
          <div className="mb-6 rounded-xl border border-emerald-800/50 bg-emerald-950/30 p-4 flex gap-3">
            <CheckCircle2 className="text-emerald-400 shrink-0" size={20} />
            <p className="text-sm text-emerald-200">Payment received — thank you!</p>
          </div>
        )}

        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">Order {order.id.slice(0, 8)}</h1>
            <p className="text-sm text-slate-500 mt-1">{new Date(order.created_at).toLocaleString()}</p>
          </div>
          <span className={`text-xs px-3 py-1.5 rounded-full ${ORDER_STATUS_COLORS[order.status]}`}>
            {ORDER_STATUS_LABELS[order.status]}
          </span>
        </div>

        <section className="rounded-xl border border-slate-800 bg-slate-900/50 p-4 mb-4">
          <h2 className="text-sm font-semibold text-slate-400 mb-3">Items</h2>
          <ul className="space-y-2">
            {items.map((item) => (
              <li key={item.id} className="flex justify-between text-sm">
                <span>{item.title}{item.quantity > 1 ? ` ×${item.quantity}` : ''}</span>
                <span>{formatCashPrice(item.line_cash_pence, order.currency)}</span>
              </li>
            ))}
          </ul>
          <div className="border-t border-slate-800 mt-3 pt-3 flex justify-between font-medium">
            <span>Total</span>
            <span>{formatCashPrice(order.cash_amount_pence, order.currency)}</span>
          </div>
          {order.credits_charged > 0 && (
            <p className="text-xs text-slate-500 mt-1">or {order.credits_charged} credits</p>
          )}
        </section>

        {order.stripe_checkout_session_id && (
          <a
            href={`https://dashboard.stripe.com/search?query=${order.stripe_checkout_session_id}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-sm text-violet-400 hover:text-violet-300 mb-4"
          >
            <Receipt size={14} /> View receipt in Stripe
          </a>
        )}

        {canUpload && (
          <section className="rounded-xl border border-slate-800 bg-slate-900/50 p-4 mb-4">
            <h2 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <Upload size={16} /> Upload project files
            </h2>
            <input
              ref={fileRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => handleUpload(e.target.files)}
            />
            <button
              type="button"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
              className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-500 disabled:opacity-60"
            >
              {uploading ? 'Uploading…' : 'Choose files'}
            </button>
          </section>
        )}

        {files.length > 0 && (
          <section className="rounded-xl border border-slate-800 bg-slate-900/50 p-4 mb-4">
            <h2 className="text-sm font-semibold text-white mb-3">Files</h2>
            <ul className="space-y-2">
              {files.map((f) => (
                <li key={f.id} className="flex items-center justify-between text-sm">
                  <span className="text-slate-300">
                    {f.kind === 'deliverable' ? '📦' : '📎'} {f.file_name}
                  </span>
                  <button
                    type="button"
                    onClick={() => downloadFile(f.storage_path, f.file_name)}
                    className="text-violet-400 hover:text-violet-300 flex items-center gap-1"
                  >
                    <Download size={14} /> Download
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="rounded-xl border border-slate-800 bg-slate-900/50 p-4 mb-4">
          <h2 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <MessageSquare size={16} /> Project notes
          </h2>
          <textarea
            value={customerNotes}
            onChange={(e) => setCustomerNotes(e.target.value)}
            rows={3}
            className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-white mb-2"
            placeholder="Brief, property address, special instructions…"
          />
          <button
            type="button"
            onClick={handleSaveNotes}
            className="text-sm text-violet-400 hover:text-violet-300"
          >
            Save notes
          </button>
        </section>

        <section className="rounded-xl border border-slate-800 bg-slate-900/50 p-4 mb-4">
          <h2 className="text-sm font-semibold text-white mb-3">Timeline</h2>
          <ul className="space-y-3">
            {events.map((ev) => (
              <li key={ev.id} className="text-sm border-l-2 border-slate-700 pl-3">
                <p className="text-slate-300">{ev.message}</p>
                <p className="text-xs text-slate-600 mt-0.5">{new Date(ev.created_at).toLocaleString()}</p>
              </li>
            ))}
          </ul>
          <div className="flex gap-2 mt-4">
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a message to the team"
              className="flex-1 rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm"
            />
            <button
              type="button"
              onClick={handleAddNote}
              className="rounded-lg bg-slate-700 px-3 py-2 text-sm hover:bg-slate-600"
            >
              Send
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
